import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ContentfulMedia } from '@wbk/contentful';
import { WebsiteConfig } from '@wbk/contentful/api';
import useLocalization from '@/context/localization/useLocalization';
import MobileMenu from './MobileMenu';
import { MenuOverlaySvg } from './HeaderCover';

type Props = {
  isOpen: boolean;
  toggleOpen: (n: number) => void;
  ref: React.RefObject<HTMLDivElement | null>;
  config: WebsiteConfig;
  onMouseLeave?: (e: React.MouseEvent) => void;
  handleMouseEnter?: () => void;
  isHovered: boolean;
  toggleOpenGlobalSearch: () => void;
  isGlobalSearchOpen?: boolean;
  scrolled: boolean;
};

const Menu = ({
  isOpen,
  toggleOpen,
  ref,
  config,
  isHovered,
  onMouseLeave,
  handleMouseEnter,
  toggleOpenGlobalSearch,
  isGlobalSearchOpen,
  scrolled,
}: Props) => {
  const { locale } = useLocalization();

  return (
    <>
      <AnimatePresence>
        {(isHovered || isOpen) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className='absolute start-0 z-20 hidden w-full md:top-7 md:block md:px-8 xl:top-8 xl:px-10 2xl:top-[30px]'
          >
            <div
              onMouseLeave={onMouseLeave}
              onMouseEnter={handleMouseEnter}
              ref={ref}
              className='relative h-full w-full'
            >
              <MenuOverlaySvg className='aspect-[1344/472] w-full -translate-y-2 md:h-[400px] lg:h-[440px] 2xl:h-auto 2xl:translate-y-0' />
              <ul className='container absolute inset-x-0 top-24 z-[99] space-y-6 py-10'>
                <li>
                  <ul className='grid grid-cols-7 gap-6'>
                    {config.header.zonesCollection?.items?.map((zone) => {
                      if (!zone) return null;

                      return (
                        <li key={zone.slug}>
                          <Link
                            to={`/${locale}/zones/${zone.slug}`}
                            className='transition hover:grayscale-[60%]'
                            onClick={() => {
                              toggleOpen(0);
                            }}
                            // we need to force a full page reload to reset the state of the page
                            reloadDocument
                          >
                            <div className='space-y-1'>
                              <ContentfulMedia
                                media={zone?.zoneLogo}
                                alt={zone.title}
                                width={100}
                                height={100}
                                fit='pad'
                                className='mx-auto w-auto object-contain'
                              />
                              {zone?.sponsorLogo && (
                                <ContentfulMedia
                                  className='mx-auto max-h-5 w-auto object-contain'
                                  media={zone?.sponsorLogo}
                                  alt={zone.sponsorLogo?.title || ''}
                                />
                              )}
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <MobileMenu
        config={config}
        toggleOpenGlobalSearch={toggleOpenGlobalSearch}
        isGlobalSearchOpen={isGlobalSearchOpen}
        scrolled={scrolled}
      />
    </>
  );
};

export default Menu;
